import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useElectron, useIsElectron, useUpdateProgress } from "../../../../hooks/useElectron";
import type { UpdateCheckResult, UpdateChannel } from "../../../../types/electron/updates.types";

type UpdateState = "idle" | "checking" | "available" | "downloading" | "downloaded" | "installing" | "error";

const CHANNEL_LABELS: Record<UpdateChannel, string> = {
  prod: "channel.modal.prod",
  dev: "channel.modal.dev",
};

/**
 * Format bytes to a human-readable string (KB, MB, GB).
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Component to display app version info, current update channel,
 * and check/download/install updates.
 */
export function EditProfileAppInfo(): JSX.Element {
  const { t, i18n } = useTranslation();
  const isElectron = useIsElectron();
  const { version, versionLoading, updates } = useElectron();

  const [updateState, setUpdateState] = useState<UpdateState>("idle");
  const [checkResult, setCheckResult] = useState<UpdateCheckResult | null>(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [channel, setChannel] = useState<UpdateChannel | null>(null);

  // Load channel on first render
  const loadChannel = useCallback(async () => {
    if (!updates || channel !== null) return;
    try {
      const ch = await updates.getChannel();
      setChannel(ch);
    } catch { /* ignore */ }
  }, [updates, channel]);
  loadChannel();

  // Subscribe to download progress
  useUpdateProgress((progress) => {
    setDownloadProgress(progress);
  });

  const displayVersion = versionLoading
    ? t("common.loading")
    : version || t("common.not-available");

  const handleCheckUpdates = async () => {
    if (!updates) return;
    setUpdateState("checking");
    setErrorMsg(null);
    setCheckResult(null);

    try {
      const result = await updates.check();
      setCheckResult(result);
      setUpdateState(result.updateAvailable ? "available" : "idle");
    } catch {
      setUpdateState("error");
      setErrorMsg(t("form.updater.failed"));
    }
  };

  const handleDownload = async () => {
    if (!updates) return;
    setUpdateState("downloading");
    setDownloadProgress(0);
    setErrorMsg(null);

    try {
      await updates.download();
      setUpdateState("downloaded");
    } catch {
      setUpdateState("error");
      setErrorMsg(t("form.updater.failed"));
    }
  };

  const handleInstall = async () => {
    if (!updates) return;
    setUpdateState("installing");
    try {
      await updates.install();
    } catch {
      setUpdateState("error");
      setErrorMsg(t("form.updater.failed"));
    }
  };

  const formattedDate = checkResult?.publishedAt
    ? new Date(checkResult.publishedAt).toLocaleDateString(i18n.language, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const isPrerelease = checkResult?.prerelease ?? false;
  const hasNewVersion = updateState === "available" || updateState === "downloading" || updateState === "downloaded" || updateState === "installing";

  return (
    <div className="EditProfileAppInfoComponent">
      <div className="AppInfo">
        {/* Version */}
        <div className="AppInfoItem">
          <ul>
            <li>
              {t("edit.profile.app-info.version")}: <strong>{displayVersion}</strong>
            </li>
            {isElectron && channel && (
              <li>
                {t("form.updater.channel")}: <strong>{t(CHANNEL_LABELS[channel])}</strong>
              </li>
            )}
          </ul>
        </div>

        {/* Updater */}
        {isElectron && updates && (
          <div className="AppUpdater">
            {/* Check button — only when idle or error and no new version card showing */}
            {(updateState === "idle" || updateState === "error") && (
              <>
                {updateState === "idle" && checkResult && !checkResult.updateAvailable && (
                  <span className="Status success">
                    <i className="las la-check-circle" /> {t("form.updater.up-to-date")}
                  </span>
                )}
                <button className="check-btn" onClick={handleCheckUpdates}>
                  <i className="las la-sync" /> {t("form.updater.check.title")}
                </button>
              </>
            )}

            {/* Checking spinner */}
            {updateState === "checking" && (
              <span className="Status checking">
                <i className="las la-spinner la-spin" /> {t("form.updater.checking")}
              </span>
            )}

            {/* New version card */}
            {hasNewVersion && checkResult?.version && (
              <div className="UpdateCard">
                <div className="UpdateCard__header">
                  <span className="UpdateCard__title">
                    <i className="las la-arrow-circle-up" />
                    {t("form.updater.new-version")}
                  </span>
                  <span className={`UpdateCard__badge ${isPrerelease ? "UpdateCard__badge--pre" : "UpdateCard__badge--stable"}`}>
                    {isPrerelease ? t("form.updater.badge.prerelease") : t("form.updater.badge.stable")}
                  </span>
                </div>

                <div className="UpdateCard__version">v{checkResult.version}</div>

                <div className="UpdateCard__meta">
                  {formattedDate && (
                    <div className="UpdateCard__meta-item">
                      <i className="las la-calendar" />
                      <span>{t("form.updater.published-at")}: {formattedDate}</span>
                    </div>
                  )}
                  {checkResult.fileSize != null && checkResult.fileSize > 0 && (
                    <div className="UpdateCard__meta-item">
                      <i className="las la-file-archive" />
                      <span>{t("form.updater.file-size")}: {formatFileSize(checkResult.fileSize)}</span>
                    </div>
                  )}
                </div>

                {/* Download / progress / install actions within the card */}
                <div className="UpdateCard__actions">
                  {updateState === "available" && (
                    <button className="download-btn" onClick={handleDownload}>
                      <i className="las la-download" /> {t("form.updater.download")}
                    </button>
                  )}

                  {updateState === "downloading" && (
                    <div className="UpdateCard__progress">
                      <span className="Status downloading">
                        <i className="las la-spinner la-spin" />{" "}
                        {t("form.updater.progress", {
                          version: checkResult.version,
                          progress: Math.round(downloadProgress),
                        })}
                      </span>
                      <div className="progress-bar">
                        <div
                          className="progress-bar__fill"
                          style={{ width: `${downloadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {updateState === "downloaded" && (
                    <>
                      <span className="Status success">
                        <i className="las la-check-circle" /> {t("form.updater.downloaded")}
                      </span>
                      <button className="install-btn" onClick={handleInstall}>
                        <i className="las la-redo-alt" /> {t("form.updater.install")}
                      </button>
                    </>
                  )}

                  {updateState === "installing" && (
                    <span className="Status checking">
                      <i className="las la-spinner la-spin" /> {t("form.updater.install")}...
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Error */}
            {errorMsg && (
              <span className="Status error">
                <i className="las la-exclamation-triangle" /> {errorMsg}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
